/**
 * What the audio system is told each frame — one plain struct, no engine objects.
 *
 * This is the audio side of non-negotiable #7. Nothing in here is a `Vector3`, a renderer handle
 * or a reference to the flight model; it is numbers, booleans and one string. That means the same
 * struct can be filled from the local `FlightDynamicsModel` today and from a remote player's
 * network packet later, and the entire sound of another aeroplane in the same sky is already
 * implemented — it just needs a second {@link AudioSystem} pointed at a different struct.
 *
 * It is also why the voices take *state* rather than reading the sim: a voice that reached into
 * `fdm.state` could only ever make a noise for the aeroplane you are flying.
 *
 * The struct is allocated once and mutated in place, per the frame-path allocation rule.
 */

/** Everything about one aeroplane that makes a sound. SI units throughout. */
export interface AircraftAudioState {
  // --- powerplant ------------------------------------------------------------------------
  rpm: number;
  /** Shaft power actually produced [W] — load, not throttle. See `model.engineLoad`. */
  powerW: number;
  /** Throttle lever, 0…1. Used only where the *lever* is the sound, e.g. a turbine's spool-up. */
  throttle: number;
  /**
   * Reheat, 0…1. Always zero on a piston, and on a turbine it is what lights the burner layer.
   *
   * Here rather than left to the debug trigger because M6's fighter has an afterburner and the
   * sound of it is a *state* the flight model owns, not an event somebody remembers to fire.
   */
  afterburnerFraction: number;
  /** Is the engine firing? False while shut down, and while the starter is still cranking. */
  engineRunning: boolean;
  /** Is the starter motor turning the crank right now? */
  starterEngaged: boolean;

  // --- airflow ---------------------------------------------------------------------------
  indicatedAirspeedMps: number;
  trueAirspeedMps: number;
  groundSpeedMps: number;
  /** Angle of attack [rad]. Drives the buffet and the stall horn. */
  alphaRad: number;
  /** Local speed of sound [m/s], from the atmosphere model. */
  speedOfSoundMps: number;

  // --- ground ----------------------------------------------------------------------------
  onGround: boolean;
  /** Total normal force through the wheels [N]. Zero in flight, full weight parked. */
  weightOnWheelsN: number;
  /** Vertical speed at the instant of touchdown [m/s]; the gear publishes it, and it latches. */
  touchdownRateMps: number;
  /** What is under the wheels — an OurAirports surface name. */
  surface: string;

  // --- configuration ----------------------------------------------------------------------
  flapAngleDeg: number;
  /** Where the flaps are *going*, so the servo can run before the surface has arrived. */
  flapTargetDeg: number;
  /** Gear position, 0 up … 1 down. Fixed-gear aircraft sit at 1 and never move. */
  gearPosition: number;
  gearTargetPosition: number;
}

export function makeAircraftAudioState(): AircraftAudioState {
  return {
    rpm: 0,
    powerW: 0,
    throttle: 0,
    afterburnerFraction: 0,
    engineRunning: false,
    starterEngaged: false,
    indicatedAirspeedMps: 0,
    trueAirspeedMps: 0,
    groundSpeedMps: 0,
    alphaRad: 0,
    speedOfSoundMps: 340.3,
    onGround: true,
    weightOnWheelsN: 0,
    touchdownRateMps: 0,
    surface: 'asphalt',
    flapAngleDeg: 0,
    flapTargetDeg: 0,
    gearPosition: 1,
    gearTargetPosition: 1,
  };
}

/** Where the ears are, and what is between them and the aeroplane. */
export interface ListenerAudioState {
  /** 0 outside the aeroplane, 1 in the cockpit, fractional mid-transition. */
  cockpitFraction: number;
  /** How far the listener is from the aircraft [m]. */
  distanceM: number;
  /** Bearing of the aircraft, −1 hard left … +1 hard right. */
  pan: number;
  /** Doppler frequency ratio for the aircraft's sound at this listener. 1 when neither moves. */
  dopplerRatio: number;
  /** How deep in cloud the listener is, 0…1. */
  cloudiness: number;
  /** 1 while the simulation runs, 0 when paused. Ducks the world without touching the sliders. */
  activity: number;
}

export function makeListenerAudioState(): ListenerAudioState {
  return { cockpitFraction: 0, distanceM: 0, pan: 0, dopplerRatio: 1, cloudiness: 0, activity: 1 };
}

/** The weather, as far as the ear is concerned. */
export interface EnvironmentAudioState {
  /** Surface wind speed [m/s] — the bed you hear when parked. */
  surfaceWindMps: number;
  /** Precipitation intensity, 0…1. */
  precipitation: number;
}

export function makeEnvironmentAudioState(): EnvironmentAudioState {
  return { surfaceWindMps: 0, precipitation: 0 };
}

/**
 * What the armament sounds like this frame.
 *
 * Continuous state only — the seeker's tone is a *level*, not an event, so it belongs here beside
 * the engine's RPM rather than being fired from a call site. The one-shots (a round, a launch, a
 * detonation) go through {@link AudioSystem.fireCannon} and friends instead, because they happen
 * at instants and a struct cannot represent "twice this frame".
 */
export interface WeaponsAudioState {
  /** Master arm. Zero silences the seeker outright — see the weapons-cold rule. */
  armed: boolean;
  /** 0…1 from the seeker: how much signal the head is seeing. */
  seekerTone: number;
  /** True once the track has gone solid, which is what stops the growl chopping. */
  seekerLocked: boolean;
}

export function makeWeaponsAudioState(): WeaponsAudioState {
  return { armed: false, seekerTone: 0, seekerLocked: false };
}
